import React, { useState, useEffect, useRef, useMemo } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../Security/firebase-config";
import "./Favorites.css";
import { useAuth } from "../../context/AuthContext"; // Adjust the path based on your folder structure
import { useTeam } from "../../context/TeamContext";
import { useAccess } from "../../context/AccessContext";
import { hasPersonalFeature } from "../../utils/entitlements";
import CustomAlert from "./CustomAlert";
import SubscriptionMessage from "./SubscriptionMessage";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { resolveGuideAssetUrl } from "../../utils/resolveGuideAssetUrl";




const Favorites = ({ headingId, headingContent, pageLocation }) => {
  const { userData, loading } = useAuth();
  const { teamId } = useTeam();
  const { loading: accessLoading, personalEntitlements } = useAccess() || {};
  const [currentImage, setCurrentImage] = useState("/assets/Favorites/grey-star.png");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);
  const [showSubscriptionMessage, setShowSubscriptionMessage] = useState(false);
  const [subscriptionMessageConfig, setSubscriptionMessageConfig] = useState({
    message: "Please login to use this function.",
    actionLabel: "Login",
    actionPath: "/login",
  });
  const [authActionAlert, setAuthActionAlert] = useState("");
  const portalDropdownRef = useRef(null);
  const navigate = useNavigate();
  const canUseFavorites = hasPersonalFeature(
    personalEntitlements,
    "personalFavorites"
  );


  const images = useMemo(() => {
   const uid = userData?.uid || auth.currentUser?.uid || null;
   const userStore = uid ? { collection: "users", document: uid, field: "favorites" } : null;
   const teamStore = teamId ? { collection: "teams", document: teamId, field: "favorites" } : null;

   return [
     { src: "/assets/Favorites/grey-star.png",  label: "None",       storage: null },
     { src: "/assets/Favorites/blue-star.png",  label: "Individual", storage: userStore },
     { src: "/assets/Favorites/green-star.png", label: "Team",       storage: teamStore },
     { src: "/assets/Favorites/purple-star.png",label: "Both",       storage: [userStore, teamStore] },
   ];
  }, [userData?.uid, teamId]);

  const showLoginRequiredAlert = (
    message = "Please Login to use this function"
  ) => {
    setAuthActionAlert(message);
  };

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        portalDropdownRef.current &&
        !portalDropdownRef.current.contains(event.target)
      ) {
        setDropdownVisible(false);
      }
    };
  
    if (dropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
  
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownVisible]);
  

  const handleImageClick = (event) => {
    event.preventDefault(); // Prevent default behavior
    event.stopPropagation(); // Prevent the click event from propagating to parent elements
    setDropdownVisible(!dropdownVisible);
  };
  

  const handleOptionClick = async (event, image) => {
    event.preventDefault(); // Prevent default behavior
    event.stopPropagation(); // Prevent propagation to parent elements
    console.log("Selected image:", image);

    if (!auth.currentUser) {
      setDropdownVisible(false);
      showLoginRequiredAlert("Please Login to use this function");
      return;
    }

    if (accessLoading) {
      setDropdownVisible(false);
      return;
    }

    if (!canUseFavorites) {
      setDropdownVisible(false);
      setSubscriptionMessageConfig({
        message: "Join a Basic-or-higher team to save favorites on guide pages.",
        actionLabel: teamId ? "Review Team Subscription" : "Go to Team Dashboard",
        actionPath: teamId ? "/team-setup-billing" : "/team-center/dashboard",
      });
      setShowSubscriptionMessage(true);
      return;
    }

    // If they pick Team or Both but there's no active team, explain why it can't save
    const wantsTeam = image.label === "Team" || image.label === "Both";
    if (wantsTeam && !teamId) {
      setSubscriptionMessageConfig({
        message: "Join or switch to a team before saving team favorites.",
        actionLabel: "Go to Team Dashboard",
        actionPath: "/team-center/dashboard",
      });
      setShowSubscriptionMessage(true);
      return;
    }

    setDropdownVisible(false);

    const previousImage = images.find((img) => img.src === currentImage);
    console.log("Previous image:", previousImage);

    if (previousImage?.storage) {
        console.log("Removing favorite from previous storage:", previousImage.storage);
        await updateFavorites(previousImage.storage, "remove");
    }

    if (image.storage) {
        console.log("Adding favorite to new storage:", image.storage);
        await updateFavorites(image.storage, "add");
    }

    setCurrentImage(image.src);
};

  

  const updateFavorites = async (storage, action) => {
    const updateDocData = async (store) => {
      if (!store) return;

      const { collection, document, field } = store;

      console.log("Processing store:", { collection, document, field });

      if (!collection || !document) {
        console.error("Invalid Firestore reference:", { collection, document });
        return;
      }

      console.log(`Fetching document: collection=${collection}, document=${document}`);

      const docRef = doc(db, collection, document);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.error(`Document does not exist: collection=${collection}, document=${document}`);
        return;
      }

      console.log("Fetched document data:", docSnap.data());

      let favorites = docSnap.data()[field] || [];
      console.log("Existing favorites:", favorites);

      if (action === "add") {
        console.log("Adding favorite:", { headingContent, headingId, pageLocation });
        favorites.push({ headingContent, headingId, pageLocation });
      } else if (action === "remove") {
        console.log("Removing favorite:", { headingId });
        favorites = favorites.filter((item) => item.headingId !== headingId);
      }

      console.log("Updated favorites list:", favorites);

      console.log(`Updating document: collection=${collection}, document=${document}, field=${field}`);
      await updateDoc(docRef, { [field]: favorites });
      console.log("Document updated successfully.");
    };

    if (Array.isArray(storage)) {
      for (const store of storage) await updateDocData(store);
    } else {
      await updateDocData(storage);
    }
  };

  useEffect(() => {
    const fetchFavorites = async () => {
      console.log("Starting to fetch favorites...");
  
      if (!userData?.uid) {
        console.warn("[DEBUG] No authenticated user found. Skipping favorite retrieval.");
        setCurrentImage("/assets/Favorites/grey-star.png");
        return;
      }

      if (!canUseFavorites) {
        setCurrentImage("/assets/Favorites/grey-star.png");
        return;
      }
  
      let userMatch = false; // Track if a match is found in the users collection
      let teamMatch = false; // Track if a match is found in the teams collection
  
      for (const img of images) {
        if (!img.storage) {
          console.log(`Skipping image without storage: ${img.label}`);
          continue;
        }
  
        const storages = Array.isArray(img.storage) ? img.storage : [img.storage];
  
        for (const storage of storages) {
          if (!storage) continue;
  
          const { collection, document, field } = storage;
          console.log(
            `Fetching document for image "${img.label}" from: collection=${collection}, document=${document}`
          );
  
          try {
            const docSnap = await getDoc(doc(db, collection, document));
  
            if (!docSnap.exists()) {
              console.warn(
                `Document does not exist: collection=${collection}, document=${document}`
              );
              continue;
            }
  
            const favorites = docSnap.data()[field] || [];
            console.log(`Fetched favorites from Firestore for "${img.label}":`, favorites);
  
            // Check if the current headingId is in the favorites
            if (favorites.some((item) => item.headingId === headingId)) {
              console.log(
                `Match found for headingId=${headingId} in collection=${collection}.`
              );
  
              if (collection === "users") userMatch = true;
              if (collection === "teams") teamMatch = true;
            }
  
            if (userMatch && teamMatch) {
              console.log(
                "Matches found in both users and teams collections. Setting image to discordgrey."
              );
              setCurrentImage("/assets/Favorites/purple-star.png");
              return;
            }
          } catch (error) {
            console.error(`Error fetching document for image "${img.label}":`, error);
          }
        }
      }
  
      if (userMatch) {
        console.log("Match found in users collection only. Setting image to discordred.");
        setCurrentImage("/assets/Favorites/blue-star.png");
      } else if (teamMatch) {
        console.log("Match found in teams collection only. Setting image to Book-drawing.");
        setCurrentImage("/assets/Favorites/green-star.png");
      } else {
        console.log("No matches found. Setting image to discordgreen.");
        setCurrentImage("/assets/Favorites/grey-star.png");
      }
  
      console.log("Finished fetching favorites.");
    };
  
    if (loading || accessLoading) {
      console.log("[DEBUG] Skipping favorites fetch; still loading user data.");
      return;
    }
  
    fetchFavorites();
  }, [userData?.uid, teamId, headingId, loading, accessLoading, images, canUseFavorites]); 
  
  

  return (
    <div className="favorites-container" ref={dropdownRef}>
      <img
        src={resolveGuideAssetUrl(currentImage)}
        alt="Favorite Icon"
        onClick={handleImageClick}
        className="favorite-icon"
      />

      {dropdownVisible &&
        createPortal(
          <div
            className="dropdown-menu"
            ref={portalDropdownRef}
            style={{
              top: dropdownRef.current?.getBoundingClientRect().top + window.scrollY,
              left: dropdownRef.current?.getBoundingClientRect().right
            }}
          >
            {images.map((img) => (
              <div
                key={img.src}
                className="dropdown-item"
                onClick={(event) => handleOptionClick(event, img)}
              >
                <img src={resolveGuideAssetUrl(img.src)} alt={img.label} className="dropdown-icon" />
                <span>{img.label}</span>
              </div>
            ))}
          </div>,
          document.body
        )        
      }

    
      {authActionAlert &&
        createPortal(
          <CustomAlert
            type="auth"
            message={authActionAlert}
            position="top"
            autoCloseMs={3200}
            onClose={() => setAuthActionAlert("")}
            actions={[
              {
                label: "Login",
                onClick: () => {
                  setAuthActionAlert("");
                  navigate("/login");
                },
              },
            ]}
          />,
          document.body
        )}

      {showSubscriptionMessage && (
        <SubscriptionMessage
          onClose={() => setShowSubscriptionMessage(false)}
          message={subscriptionMessageConfig.message}
          actionLabel={subscriptionMessageConfig.actionLabel}
          actionPath={subscriptionMessageConfig.actionPath}
        />
      )}
    </div>
  );
};

export default Favorites;
